import { timingMode } from '@/environment';
import { useAnalytics } from '@/services/analytics';
import { useCallback } from 'react';

/**
 * Provides utilities for tracking the timing of code blocks or functions.
 *
 * The functionality includes event tracing, timing individual functions or code blocks,
 * and wrapping functions with timing mechanisms for performance monitoring.
 *
 * The default output channels for tracing can be configured based on the `timingMode` environment variable. Currently,
 * the following modes are supported:
 * - `console`: Logs event traces to the console only.
 * - `mixpanel`: Sends event traces to Mixpanel only.
 * - `console+mixpanel`: Logs and sends event traces to both the console and Mixpanel.
 * - any other value: Disables tracing.
 *
 * @param {string} [outputMode] - The output mode for tracing, defaults to the value of the `timingMode` environment variable.
 */
export function useTiming(outputMode: 'console' | 'mixpanel' | 'console+mixpanel' | string = timingMode) {
    const mixpanel = useAnalytics();

    const outputToConsole = outputMode === 'console' || outputMode === 'console+mixpanel';
    const outputToMixpanel = outputMode === 'mixpanel' || outputMode === 'console+mixpanel';
    const timingEnabled = outputToConsole || outputToMixpanel;

    const traceToConsole = useCallback((label: string, props?: Record<string, any>) => {
        let message = props?.message ? ` | ${props.message}` : '';
        let duration: string = '';

        switch (props?.timingEvent) {
            case 'start':
                message = message || ' | start';
                break;
            case 'end':
                message = message || ' | end';
                duration = props?.durationMs ? ` | duration: ${props.durationMs}ms` : '';
                break;
        }

        let filteredProps: Record<string, any> = {};
        Object.entries(props || {}).forEach(([key, value]) => {
            if (key !== 'timingEvent' && key !== 'message' && key !== 'durationMs') {
                filteredProps[key] = value;
            }
        });
        const filteredPropsString = Object.keys(filteredProps).length > 0 ? ` | ${JSON.stringify(filteredProps)}` : '';

        console.log(`[TIMING] ${label}${message}${duration}${filteredPropsString}`);
    }, []);

    const traceToMixpanel = useCallback(
        (label: string, extraProps?: Record<string, any>) => {
            mixpanel?.track('timing', {
                ...(extraProps || {}),
                label,
            });
        },
        [mixpanel]
    );

    /**
     * A function used to trace arbitrary timing-related events by logging them to multiple output channels.
     *
     * This function supports sending event traces to the browser console and/or Mixpanel, depending on the
     * value of the `timingMode` environment variable.
     *
     * @param {string} label - The label of the code block or operation being traced, such as "getAllVoices".
     * @param {Record<string, any>} [extraProps] - Optional additional properties or metadata
     * associated with the event to provide more context during traces.
     * @param {string} [extraProps.message] - Optional message to be included in the trace output (especially useful for console logging).
     */
    const trace = useCallback(
        (label: string, extraProps?: Record<string, any>) => {
            if (outputToConsole) {
                traceToConsole(label, extraProps);
            }
            if (outputToMixpanel) {
                traceToMixpanel(label, extraProps);
            }
        },
        [outputToConsole, outputToMixpanel, traceToConsole, traceToMixpanel]
    );

    /**
     * A higher-order function used to execute the passed function with timing instrumentation.
     *
     * This utility allows tracking the performance of an operation and reporting metrics such as execution
     * time, success, or failure. This function can be used with both synchronous and asynchronous operations.
     *
     * @template T - The return type of the callback function.
     *
     * @param {string} label - A label for the code block or operation to be tracked.
     * @param {() => T | Promise<T>} fn - The callback function whose execution is being timed.
     * @param {Record<string, any>} [extraProps] - Additional properties or metadata to be recorded with timing events.
     * @param {string} [extraProps.message] - An optional message to be included in the trace output.
     * @returns {T | Promise<T>} The result of executing the provided callback function, or its resolved/rejected promise.
     */
    const withTiming = useCallback(
        <T,>(label: string, fn: () => T | Promise<T>, extraProps: Record<string, any> = {}): T | Promise<T> => {
            if (!timingEnabled) {
                return fn();
            }

            const start = performance.now();
            trace(label, {
                ...extraProps,
                timingEvent: 'start',
            });

            const handleEnd = (value: T) => {
                const end = performance.now();
                trace(label, {
                    ...extraProps,
                    timingEvent: 'end',
                    event: 'success',
                    durationMs: Math.round(end - start),
                });
                return value;
            };

            const handleError = (err: Error) => {
                const end = performance.now();
                trace(label, {
                    ...extraProps,
                    timingEvent: 'end',
                    event: 'error',
                    durationMs: Math.round(end - start),
                    error: err.message,
                });
                throw err;
            };

            let result: T | Promise<T>;
            try {
                result = fn();
            } catch (err: any) {
                return handleError(err); // will throw again
            }

            if (result instanceof Promise) {
                return result.then(handleEnd).catch(handleError);
            } else {
                return handleEnd(result);
            }
        },
        [timingEnabled, trace]
    );

    /**
     * A function that wraps a given asynchronous or synchronous function with timing instrumentation.
     *
     * It returns a new function that automatically tracks the execution time of the given function and logs it
     * against a specified event name, along with any extra properties provided.
     *
     * @param {string} label - The label of the function being traced, such as "getAllVoices".
     * @param {(...args: Args) => T | Promise<T>} fn - The function to be wrapped with timing instrumentation.
     * @param {Record<string, any>} [extraProps] - Additional properties to log alongside the timing data.
     * @returns {(...args: Args) => T | Promise<T>} A new function that performs the original functionality while instrumenting timing data around it.
     */
    const wrapWithTiming = useCallback(
        <T, Args extends any[]>(
            label: string,
            fn: (...args: Args) => T | Promise<T>,
            extraProps: Record<string, any> = {}
        ): ((...args: Args) => T | Promise<T>) => {
            return (...args: Args) => withTiming(label, () => fn(...args), extraProps);
        },
        [withTiming]
    );

    return {
        timingEnabled,
        trace,
        withTiming,
        wrapWithTiming,
    };
}
